import React from "react";
import UserDetailsCard from "./UserDetailsCard";
import LocationCard from "./LocationCard";
import TitleCard from "./TitleCard";
import BaseWrapper from "../../components/ui/BaseWrapper";
import { useSelector } from "react-redux";
import ScheduleCard from "./ScheduleCard";
import useAuth from "../../hooks/useAuth";
import PaymentCard from "./PaymentCard";
import WorkingHoursCard from "./WorkingHoursCard";
import CurrentStatusCard from "../../components/CurrentStatusCard";
import { UserType, WorkOrderStatusType } from "../../utils/enum";
import dayjs from "dayjs";
import { getTimeZoneAbbreviation } from "../../utils/getTimeZoneAbbreviation";
import { useLocation, useSearchParams } from "react-router-dom";

const WorkJobDetailsHeader = ({ userAllowedAction }) => {
  const { authRole, auth } = useAuth();
  const [searchParams] = useSearchParams();

  const fromCustomerBoard = Boolean(searchParams.get("fromCustomerBoard"));

  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  console.log("🚀 ~ WorkJobDetailsHeader ~ workOrderDetail:", workOrderDetail);

  const isWorkDoneReviewed =
    workOrderDetail?.status === "WORKDONE" && workOrderDetail?.subStatus === "REVIEWED";

  const companyOwnerId = workOrderDetail?.assignedFreelanceCompany?.owner?.id;
  const isCompanyOwnerOfThisWO =
    authRole === UserType.FREELANCER && auth?.user?.id === companyOwnerId;

  const isFreelanceCompanyEngineer =
    authRole === UserType.FREELANCER &&
    !isCompanyOwnerOfThisWO &&
    (userAllowedAction?.isAssignedMember || !!workOrderDetail?.assignedFreelanceCompanyId);

  const baseTitleStatus =
    authRole !== UserType.CLIENT && userAllowedAction?.isDeclined
      ? "DECLINED"
      : authRole !== UserType.CLIENT &&
          (userAllowedAction?.isRouted || userAllowedAction?.isAccepted)
        ? "ROUTED"
        : authRole !== UserType.CLIENT && userAllowedAction?.isCounter
          ? "COUNTER"
          : authRole !== UserType.CLIENT && userAllowedAction?.isRequested
            ? "REQUESTED"
            : userAllowedAction?.isWorkOrderCancelByClient ||
                userAllowedAction?.isWorkOrderCancelByEngineer
              ? "CANCELLED"
              : isCompanyOwnerOfThisWO && workOrderDetail?.status === "INPROGRESS"
                ? "ASSIGNED"
                : workOrderDetail?.status;

  const titleStatus =
    (authRole === UserType.FREELANCER || authRole === UserType.CUSTOMER) &&
    baseTitleStatus === WorkOrderStatusType.VERIFIED
      ? WorkOrderStatusType.WORKDONE
      : baseTitleStatus;

  const assignedToAnotherEngineer =
    authRole === UserType.FREELANCER &&
    userAllowedAction?.isWorkOrderAlreadyAssigned &&
    !userAllowedAction?.isDeclined &&
    (userAllowedAction?.isRequested ||
      userAllowedAction?.isCounter ||
      userAllowedAction?.isRouted ||
      userAllowedAction?.isAccepted);

  const effectiveSubStatus =
    (isCompanyOwnerOfThisWO || authRole === UserType.FREELANCER) &&
    workOrderDetail?.subStatus === "REVIEWED"
      ? null
      : workOrderDetail?.subStatus;

  const scheduleTask =
    effectiveSubStatus === WorkOrderStatusType.ON_THE_WAY
      ? workOrderDetail?.tasks?.find((task) => task.taskType === WorkOrderStatusType.ON_THE_WAY)
      : effectiveSubStatus === WorkOrderStatusType.RUNNING_LATE
        ? workOrderDetail?.tasks?.find((task) => task.taskType === WorkOrderStatusType.RUNNING_LATE)
        : effectiveSubStatus === WorkOrderStatusType.CHECK_IN
          ? workOrderDetail?.tasks?.find((task) => task.taskType === WorkOrderStatusType.CHECK_IN)
          : effectiveSubStatus === WorkOrderStatusType.CHECK_OUT &&
            workOrderDetail?.tasks?.find((task) => task.taskType === WorkOrderStatusType.CHECK_OUT);

  const scheduleText = scheduleTask?.completedTask?.text
    ? `${dayjs(scheduleTask.completedTask.text).format("dddd, DD/MM/YYYY")} at ${dayjs(
        scheduleTask.completedTask.text,
      )
        .tz(workOrderDetail?.timeZone)
        .format("hh:mm A")} (${getTimeZoneAbbreviation(workOrderDetail?.timeZone)})`
    : null;

  // Only show routed member when the company owner themselves created the assignment
  const routedOffer = workOrderDetail?.assignedFreelanceCompanyId
    ? workOrderDetail?.offers?.find(
        (o) =>
          (o.status === "ROUTED" || o.status === "ACCEPTED" || o.status === "ASSIGNED") &&
          o.user?.id !== companyOwnerId &&
          o.createdById === companyOwnerId,
      )
    : null;
  const isRoutedEngineerSelf = routedOffer?.user?.id === auth?.user?.id;
  // Freelancers only see the assigned member when they own the company this WO is assigned to
  const canSeeRoutedEngineer = authRole !== UserType.FREELANCER || isCompanyOwnerOfThisWO;
  const routedEngineer =
    canSeeRoutedEngineer && routedOffer && !isRoutedEngineerSelf ? routedOffer.user : null;

  const displayText = (() => {
    if (workOrderDetail?.customerSubStatus === WorkOrderStatusType.CANCELLED) {
      return "Cancelled, but payment is still due to the customer";
    }
    if (effectiveSubStatus === WorkOrderStatusType.ON_THE_WAY) {
      if (authRole === UserType.FREELANCER) {
        return scheduleTask?.completedTask?.text
          ? `Marked at: ${scheduleText}`
          : "Provider is on the way to the work order location";
      }
      return "Provider is on the way to the work order location";
    }

    if (effectiveSubStatus === WorkOrderStatusType.RUNNING_LATE) {
      // The engineer types a reason when they mark themselves late. That reason is
      // the whole tooltip -- it was being stored and never shown anywhere.
      const reason = scheduleTask?.reason?.trim();
      if (reason) {
        return `Reason: ${reason}`;
      }

      if (authRole === UserType.FREELANCER) {
        return scheduleTask?.completedTask?.text
          ? `Marked at: ${scheduleText}`
          : "Provider is running late";
      }
      return "Provider is running late";
    }

    // Both audiences call it an offer; only the point of view differs.
    const isCustomerViewer = authRole === UserType.CUSTOMER;
    if (effectiveSubStatus === WorkOrderStatusType.COUNTER_OFFER_PENDING) {
      return isCustomerViewer
        ? "Your provider sent you an offer with different rates, awaiting your answer"
        : "Offer sent to the customer, awaiting their answer";
    }
    if (effectiveSubStatus === WorkOrderStatusType.COUNTER_OFFER_ACCEPTED) {
      return isCustomerViewer ? "You accepted the offer" : "The customer accepted the offer";
    }
    if (effectiveSubStatus === WorkOrderStatusType.COUNTER_OFFER_DECLINED) {
      return isCustomerViewer ? "You declined the offer" : "The customer declined the offer";
    }

    return scheduleText;
  })();

  return (
    <div className="border-b border-zinc-300 py-2 lg:py-4 bg-white z-[3]">
      <BaseWrapper>
        <div className="grid grid-cols-12 items-center">
          <div className="xl:col-span-5 col-span-12 h-full w-full flex items-center lg:ml-0 ml-4 lg:mb-0 mb-2">
            <TitleCard
              publishedDate={workOrderDetail?.createdAt}
              title={workOrderDetail?.title}
              id={workOrderDetail?.referenceId}
              status={titleStatus}
              subStatus={effectiveSubStatus}
              customerStatus={workOrderDetail?.customerStatus}
              customerSubStatus={
                authRole === UserType.CUSTOMER ? null : workOrderDetail?.customerSubStatus
              }
              fromCustomerBoard={fromCustomerBoard}
              subStatusText={displayText}
              isPOPending={effectiveSubStatus === WorkOrderStatusType.PO_PENDING}
            />
            <div></div>
          </div>

          <div className="xl:col-span-7 col-span-12 xl:mt-0 mt-2">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {workOrderDetail?.status === "WORKDONE" ||
              workOrderDetail?.status === "VERIFIED" ||
              workOrderDetail?.status === "APPROVED" ||
              workOrderDetail?.status === "PAID" ? (
                <div>
                  <WorkingHoursCard totalHours={workOrderDetail?.totalHours} />
                </div>
              ) : (userAllowedAction?.isWorkOrderCancelByEngineer ||
                  userAllowedAction?.isWorkOrderCancelByClient) &&
                !assignedToAnotherEngineer ? (
                <div>
                  <CurrentStatusCard
                    status="Cancelled"
                    tagline={
                      userAllowedAction?.showCancellationReason
                        ? userAllowedAction?.isWorkOrderCancelByEngineer
                          ? "The work order has been cancelled by engineer."
                          : "The work order has been cancelled by client."
                        : "The work order has been cancelled."
                    }
                  />
                </div>
              ) : (
                <div>
                  <ScheduleCard
                    timeLogs={workOrderDetail?.timeLogs}
                    tasks={workOrderDetail?.tasks}
                    status={workOrderDetail?.status}
                    subStatus={effectiveSubStatus}
                    engineer={workOrderDetail?.engineer}
                    schedule={{
                      timeZone: workOrderDetail?.address?.timeZone,
                      scheduleType: workOrderDetail?.scheduleType,
                      orderStartDate: workOrderDetail?.orderStartDate,
                      orderStartTime: workOrderDetail?.orderStartTime,
                      orderEndDate: workOrderDetail?.orderEndDate,
                      orderEndTime: workOrderDetail?.orderEndTime,
                      confirmedWorkOrderAt: workOrderDetail?.tasks?.find(
                        (task) => task.taskType === "START_TIME",
                      )?.completedTask?.text,
                    }}
                  />
                </div>
              )}
              {/* {authRole === "FREELANCER" ? ( */}

              {/* )  */}

              {/* : ( */}
              {/* <TimeLoggedCard /> */}
              {/* )} */}
              {(workOrderDetail?.status === "CANCELLED" &&
                userAllowedAction?.isWorkOrderCancelByClient) ||
              workOrderDetail?.status === "INCOMPLETE" ||
              workOrderDetail?.status === "REVIEWED" ||
              workOrderDetail?.status === "VERIFIED" ||
              workOrderDetail?.status === "APPROVED" ||
              workOrderDetail?.status === "PAID" ||
              workOrderDetail?.status === "WORKDONE" ? (
                <div>
                  <PaymentCard
                    status={
                      workOrderDetail?.subStatus === "REVIEWED" && authRole === "CLIENT"
                        ? "Reviewed"
                        : workOrderDetail?.status === "WORKDONE" ||
                            workOrderDetail?.status === "INCOMPLETE"
                          ? "Estimated"
                          : workOrderDetail?.status === "VERIFIED" && authRole === "CLIENT"
                            ? "Verified"
                            : workOrderDetail?.status === "APPROVED"
                              ? "Approved"
                              : workOrderDetail?.status === "INVOICED" && authRole === "CLIENT"
                                ? "Invoiced"
                                : workOrderDetail?.status === "PAID" && authRole === "CLIENT"
                                  ? "Paid"
                                  : workOrderDetail?.status === "CANCELLED"
                                    ? "Pending"
                                    : workOrderDetail?.status === "VERIFIED" &&
                                        authRole === "FREELANCER"
                                      ? "Estimated"
                                      : ""
                    }
                    payment={{
                      totalAmount: workOrderDetail?.totalAmount,
                    }}
                    hideAmount={isFreelanceCompanyEngineer}
                  />
                </div>
              ) : isWorkDoneReviewed && authRole === "CLIENT" ? (
                <div>
                  <ScheduleCard
                    timeLogs={workOrderDetail?.timeLogs}
                    tasks={workOrderDetail?.tasks}
                    status={workOrderDetail?.status}
                    subStatus={effectiveSubStatus}
                    engineer={workOrderDetail?.engineer}
                    schedule={{
                      timeZone: workOrderDetail?.address?.timeZone,
                      scheduleType: workOrderDetail?.scheduleType,
                      orderStartDate: workOrderDetail?.orderStartDate,
                      orderStartTime: workOrderDetail?.orderStartTime,
                      orderEndDate: workOrderDetail?.orderEndDate,
                      orderEndTime: workOrderDetail?.orderEndTime,
                      confirmedWorkOrderAt: workOrderDetail?.tasks?.find(
                        (task) => task.taskType === "START_TIME",
                      )?.completedTask?.text,
                    }}
                  />
                </div>
              ) : (
                <>
                  {workOrderDetail?.status === "CANCELLED" &&
                  userAllowedAction?.isWorkOrderCancelByEngineer ? (
                    <div>
                      <ScheduleCard
                        timeLogs={workOrderDetail?.timeLogs}
                        tasks={workOrderDetail?.tasks}
                        status={workOrderDetail?.status}
                        subStatus={effectiveSubStatus}
                        engineer={workOrderDetail?.engineer}
                        schedule={{
                          timeZone: workOrderDetail?.address?.timeZone,
                          scheduleType: workOrderDetail?.scheduleType,
                          orderStartDate: workOrderDetail?.orderStartDate,
                          orderStartTime: workOrderDetail?.orderStartTime,
                          orderEndDate: workOrderDetail?.orderEndDate,
                          orderEndTime: workOrderDetail?.orderEndTime,
                          confirmedWorkOrderAt: workOrderDetail?.tasks?.find(
                            (task) => task.taskType === "START_TIME",
                          )?.completedTask?.text,
                        }}
                      />
                    </div>
                  ) : (
                    <div>
                      <LocationCard location={workOrderDetail?.address} />
                    </div>
                  )}
                </>
              )}
              <div>
                <UserDetailsCard
                  engineer={workOrderDetail?.engineer}
                  engineerCompany={workOrderDetail?.engineerCompany}
                  authRole={authRole}
                  createdBy={workOrderDetail?.createdBy}
                  workOrderManager={workOrderDetail?.workOrderManager}
                  offersCount={workOrderDetail?.offersCount}
                  routedEngineer={routedEngineer}
                />
              </div>
            </div>
          </div>
        </div>
      </BaseWrapper>
    </div>
  );
};

export default WorkJobDetailsHeader;
